import React, { useState } from "react";
import { Sparkles, Loader2, Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import Modal, { ModalHeader } from "./Modal";

export default function MitigationCoachButton({ projectId, risk, locked, onLocked, onApplied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coach, setCoach] = useState(null);
  const [applying, setApplying] = useState(false);

  const close = () => setOpen(false);

  const run = async () => {
    if (locked) { onLocked && onLocked(); return; }
    setOpen(true); setLoading(true); setCoach(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/risks/${risk.id}/coach-mitigation`);
      setCoach(data);
    } catch (e) { toast.error(errMsg(e)); setOpen(false); }
    setLoading(false);
  };

  const apply = async () => {
    if (!coach?.proposed) return;
    setApplying(true);
    try {
      await api.put(`/projects/${projectId}/risks/${risk.id}`, { data: { mitigation: coach.proposed } });
      toast.success("Mitigation updated");
      close();
      onApplied && onApplied(coach.proposed);
    } catch (e) { toast.error(errMsg(e)); }
    setApplying(false);
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); run(); }}
        title="AI Mitigation Coach — rewrite into a sharper plan"
        data-testid={`coach-mitigation-${risk.id}`}
        className="text-blue-300/80 hover:text-blue-200"
      >
        <Sparkles size={13}/>
      </button>
      <Modal open={open} onClose={close} testId={`coach-modal-${risk.id}`}>
        <ModalHeader icon={Sparkles} title="Mitigation coach" onClose={close} testId={`coach-close-${risk.id}`} />
        {loading && (
          <div className="flex items-center gap-2 text-slate-300 py-6"><Loader2 className="animate-spin" size={16}/> Rewriting into a sharper plan…</div>
        )}
        {!loading && coach && (
          <>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Risk</div>
            <div className="text-sm text-slate-200 mb-4">{risk.description || "—"}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Current mitigation</div>
            <div className="text-sm text-slate-400 mb-4 italic">{coach.current || "(empty)"}</div>
            <div className="text-[10px] uppercase tracking-widest text-blue-300/80 mb-1">Suggested mitigation</div>
            <div className="text-sm text-white border border-blue-500/40 bg-blue-500/5 rounded-lg p-3" data-testid={`coach-proposed-${risk.id}`}>{coach.proposed}</div>
            {coach.rationale && <div className="text-[11px] text-slate-500 mt-2">↳ {coach.rationale}</div>}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={run} className="text-sm text-slate-400 hover:text-white px-3 py-2">Regenerate</button>
              <button onClick={apply} disabled={applying} data-testid={`coach-apply-${risk.id}`}
                className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50">
                {applying ? <Loader2 className="animate-spin" size={12}/> : <Check size={12}/>} Replace mitigation
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
